import React, { useState, useRef, useEffect } from 'react';
import { X, AlertCircle, AtSign, User, Loader } from 'lucide-react';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';
import { toast } from 'react-hot-toast';
import communityAPI from '../../services/community.api';

const CreatePostModal = ({ onClose, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentions, setMentions] = useState([]);
  const textareaRef = useRef(null);
  const { createPost } = useCommunityStore();
  const { user } = useAuthStore();

  // Handle mention detection
  const handleContentChange = (e) => {
    const value = e.target.value;
    setContent(value);
    
    const lastChar = value.slice(-1);
    const secondLastChar = value.slice(-2, -1);
    
    if (lastChar === '@' && (value.length === 1 || secondLastChar === ' ' || secondLastChar === '\n')) {
      setShowMentions(true);
      setMentionQuery('');
      setSelectedMentionIndex(0);
    } 
    else if (showMentions && mentionQuery !== null) {
      const lines = value.split('\n');
      const currentLine = lines[lines.length - 1];
      const atIndex = currentLine.lastIndexOf('@');
      
      if (atIndex !== -1) {
        const query = currentLine.substring(atIndex + 1);
        setMentionQuery(query);
        
        if (query.length > 0) {
          searchMentions(query);
        } else {
          setMentionResults([]);
        }
      } else {
        setShowMentions(false);
        setMentionQuery('');
      }
    }
  };

  const searchMentions = async (query) => {
    try {
      const response = await communityAPI.searchUsers({ query, limit: 5 });
      setMentionResults(response.data.users || []);
    } catch (error) {
      console.error('Search mentions error:', error);
      setMentionResults([]);
    }
  };

  const selectMention = (user) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const text = content;
    
    const beforeCursor = text.substring(0, cursorPos);
    const lastAtPos = beforeCursor.lastIndexOf('@');
    
    if (lastAtPos !== -1) {
      const beforeAt = text.substring(0, lastAtPos);
      const afterCursor = text.substring(cursorPos);
      const mentionText = `@${user.firstName} ${user.lastName}`;
      
      setContent(beforeAt + mentionText + ' ' + afterCursor);
      
      setMentions(prev => [...prev, { 
        id: user.id, 
        displayName: `${user.firstName} ${user.lastName}`,
        username: user.username || `${user.firstName.toLowerCase()}${user.lastName.toLowerCase()}`
      }]);
      
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = beforeAt.length + mentionText.length + 1;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
    
    setShowMentions(false);
    setMentionQuery('');
    setMentionResults([]);
  };

  const handleKeyDown = (e) => {
    if (showMentions && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < mentionResults.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev > 0 ? prev - 1 : mentionResults.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        selectMention(mentionResults[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        setMentionResults([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (content.trim().length < 1) {
      toast.error('Please add some content to your post');
      return;
    }

    setIsSubmitting(true);
    try {
      const formattedMentions = mentions.map(m => ({
        userId: m.id,
        displayName: m.displayName,
        username: m.username || `${m.displayName?.split(' ')[0]?.toLowerCase()}`
      }));

      const metadata = {
        mentions: formattedMentions,
        wordCount: content.trim().split(/\s+/).length,
        hasMedia: false,
        mediaCount: 0
      };

      const post = await createPost(content, null, metadata);
      
      if (formattedMentions.length > 0 && user) {
        for (const mention of formattedMentions) {
          if (mention.userId !== user?.id) {
            try {
              await communityAPI.createMentionNotification({
                mentionedUserId: mention.userId,
                postId: post.id
              });
            } catch (error) {
              console.error('Failed to create mention notification:', error);
            }
          }
        }
      }

      setContent('');
      setMentions([]);
      
      if (onPostCreated) {
        onPostCreated();
      }
      
      onClose();
      toast.success('Post created successfully!');
    } catch (error) {
      console.error('Create post error:', error);
      toast.error(error.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = content.length;
  const maxCharacters = 1000;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 border border-primary-100/30">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <div className="flex items-center mb-2">
                <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Create Post
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={handleContentChange}
                    onKeyDown={handleKeyDown}
                    placeholder="What's on your mind? Share your business insights, tips, or questions... Use @ to mention someone..."
                    className="block w-full px-4 py-3 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 min-h-[150px] resize-none"
                    maxLength={maxCharacters}
                    disabled={isSubmitting}
                  />
                  
                  {/* Mentions Dropdown */}
                  {showMentions && (
                    <div className="absolute z-10 mt-2 w-full bg-white rounded-xl shadow-neomorph border border-gray-100 max-h-64 overflow-y-auto">
                      {mentionResults.length > 0 ? (
                        mentionResults.map((user, index) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => selectMention(user)}
                            className={`flex items-center w-full px-4 py-3 text-left hover:bg-[#E8ECF5] transition-colors ${
                              index === selectedMentionIndex ? 'bg-[#E8ECF5]' : ''
                            }`}
                          >
                            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-md flex items-center justify-center mr-3">
                              {user.avatar ? (
                                <img 
                                  src={user.avatar} 
                                  alt={user.firstName}
                                  className="h-8 w-8 rounded-lg object-cover"
                                />
                              ) : (
                                <span className="text-white text-xs font-semibold">
                                  {user.firstName[0]}
                                </span>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-800">
                                {user.firstName} {user.lastName}
                              </div>
                              {user.businessTags && user.businessTags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  <span className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded-lg border border-primary-200 shadow-neomorph-inset">
                                    {user.businessTags[0]}
                                  </span>
                                </div>
                              )}
                            </div>
                          </button>
                        ))
                      ) : mentionQuery.length > 0 ? (
                        <div className="px-4 py-4 text-sm text-gray-500 text-center">
                          No users found for "{mentionQuery}"
                        </div>
                      ) : (
                        <div className="px-4 py-4 text-sm text-gray-500 text-center">
                          Start typing to search for users...
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-[#E8ECF5] px-3 py-1.5 rounded-lg shadow-neomorph-inset">
                        <span className={`text-xs font-medium ${
                          characterCount > maxCharacters * 0.9 ? 'text-orange-600' : 'text-gray-600'
                        }`}>
                          {characterCount}/{maxCharacters}
                        </span>
                      </div>
                      
                      {mentions.length > 0 && (
                        <div className="flex items-center bg-primary-50 px-3 py-1.5 rounded-lg shadow-neomorph-inset">
                          <AtSign className="h-3.5 w-3.5 text-primary-600 mr-1.5" />
                          <span className="text-xs font-medium text-primary-700">
                            {mentions.length} mention{mentions.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>

                    {characterCount > maxCharacters * 0.9 && (
                      <div className="flex items-center bg-orange-50 px-3 py-1.5 rounded-lg shadow-neomorph-inset">
                        <AlertCircle className="h-3.5 w-3.5 text-orange-600 mr-1.5" />
                        <span className="text-xs font-medium text-orange-700">
                          Approaching character limit
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="px-4 py-2.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || content.trim().length < 1}
                    className="px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      'Post to FastConnect'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default CreatePostModal;